import React, { useRef } from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'framer-motion';
import { IMAGES } from '../constants/images';
import { TartaWordmark } from './TartaWordmark';
export function Unveiling() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });
  // Lid: slides straight up and off
  const lidY = useTransform(scrollYProgress, [0, 1], ['0%', '-180%']);
  const lidScale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
  const lidOpacity = useTransform(scrollYProgress, [0.7, 0.95], [1, 0]);
  // Light bloom — intensifies as lid leaves, then settles
  const bloomOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0.7]);
  const bloomScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.4, 1.2]);
  // Light rays — appear during the lift, fade as cheesecake settles
  const raysOpacity = useTransform(scrollYProgress, [0.15, 0.5, 0.85], [0, 0.5, 0]);
  const raysRotate = useTransform(scrollYProgress, [0, 1], [0, 30]);
  // Cheesecake brightness/scale
  const cakeScale = useTransform(scrollYProgress, [0, 1], [0.96, 1.02]);
  const cakeBrightness = useTransform(scrollYProgress, [0, 0.6], [0.7, 1]);
  const cakeFilter = useTransform(cakeBrightness, v => `brightness(${v})`);
  // Captions
  const capLift = useTransform(scrollYProgress, [0, 0.15, 0.32], [1, 1, 0]);
  const capCrown = useTransform(scrollYProgress, [0.32, 0.45, 0.65, 0.78], [0, 1, 1, 0]);
  const capHeaven = useTransform(scrollYProgress, [0.78, 0.9], [0, 1]);
  return <section ref={ref} className="relative bg-texture-espresso text-cream" style={{
    height: '260vh'
  }} data-id="element-257">
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden" data-id="element-258">
        {/* Eyebrow */}
        <div className="absolute top-32 lg:top-40 left-1/2 -translate-x-1/2 flex items-center gap-3 text-caramel text-[10px] tracking-[0.5em] uppercase z-20 whitespace-nowrap" data-id="element-259">
          <span className="w-10 h-px bg-caramel/40" data-id="element-260" />
          The Unveiling
          <span className="w-10 h-px bg-caramel/40" data-id="element-261" />
        </div>

        {/* Stage */}
        <div className="relative" style={{
        width: 'min(82vw, 460px)',
        aspectRatio: '1 / 1'
      }} data-id="element-262">
          {/* Caramel light bloom — behind the lid */}
          <motion.div aria-hidden className="absolute -inset-[40%] rounded-full pointer-events-none" style={{
          opacity: bloomOpacity,
          scale: bloomScale,
          background: 'radial-gradient(circle at 50% 50%, rgba(255,180,110,0.45) 0%, rgba(185,130,74,0.25) 30%, transparent 70%)',
          filter: 'blur(40px)'
        }} data-id="element-263" />

          {/* Soft white core flash */}
          <motion.div aria-hidden className="absolute -inset-[10%] rounded-full pointer-events-none" style={{
          opacity: bloomOpacity,
          background: 'radial-gradient(circle at 50% 50%, rgba(255,235,210,0.25) 0%, transparent 55%)',
          filter: 'blur(20px)'
        }} data-id="element-264" />

          {/* Light rays — radial streaks */}
          <motion.div aria-hidden className="absolute -inset-[30%] pointer-events-none" style={{
          opacity: raysOpacity,
          rotate: raysRotate,
          background: `conic-gradient(from 0deg at 50% 50%,
                transparent 0deg,
                rgba(255,210,160,0.18) 8deg,
                transparent 16deg,
                transparent 38deg,
                rgba(255,210,160,0.12) 46deg,
                transparent 54deg,
                transparent 90deg,
                rgba(255,210,160,0.18) 98deg,
                transparent 106deg,
                transparent 142deg,
                rgba(255,210,160,0.10) 150deg,
                transparent 158deg,
                transparent 190deg,
                rgba(255,210,160,0.16) 198deg,
                transparent 206deg,
                transparent 250deg,
                rgba(255,210,160,0.14) 258deg,
                transparent 266deg,
                transparent 310deg,
                rgba(255,210,160,0.10) 318deg,
                transparent 326deg,
                transparent 360deg)`,
          maskImage: 'radial-gradient(circle at 50% 50%, black 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 30%, transparent 75%)',
          filter: 'blur(2px)'
        }} data-id="element-265" />

          {/* Drifting particles */}
          <Particles progress={scrollYProgress} data-id="element-266" />

          {/* Cheesecake — always rendered, brightens as lid leaves */}
          <motion.div className="absolute inset-[6%] rounded-full overflow-hidden" style={{
          scale: cakeScale,
          filter: cakeFilter,
          boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(247,240,232,0.15)'
        }} data-id="element-267">
            <img src={IMAGES.cheesecake} alt="TARTA cheesecake — revealed" className="absolute left-1/2 top-1/2 max-w-none" style={{
            width: '108%',
            height: '108%',
            transform: 'translate(-50%, -50%)',
            objectFit: 'cover'
          }} data-id="element-268" />
            {/* Inner vignette */}
            <div aria-hidden className="absolute inset-0 rounded-full pointer-events-none" style={{
            boxShadow: 'inset 0 0 80px rgba(0,0,0,0.45)'
          }} data-id="element-269" />
            {/* Caramel sheen */}
            <div aria-hidden className="absolute inset-0 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 50% 30%, rgba(255,200,140,0.18), transparent 60%)',
            mixBlendMode: 'overlay'
          }} data-id="element-270" />
          </motion.div>

          {/* Lid — slides straight up */}
          <Lid y={lidY} scale={lidScale} opacity={lidOpacity} data-id="element-271" />
        </div>

        {/* Captions */}
        <div className="absolute bottom-28 lg:bottom-36 left-0 right-0 px-8 pointer-events-none z-20" data-id="element-272">
          <div className="relative h-10 text-center" data-id="element-273">
            <motion.p className="absolute inset-x-0 font-serif italic text-cream/80 text-xl lg:text-2xl" style={{
            opacity: capLift
          }} data-id="element-274">
              Lift the lid.
            </motion.p>
            <motion.p className="absolute inset-x-0 font-serif italic text-cream/85 text-xl lg:text-2xl" style={{
            opacity: capCrown
          }} data-id="element-275">
              A deeply caramelized crown.
            </motion.p>
            <motion.p className="absolute inset-x-0 font-serif italic text-cream/90 text-xl lg:text-2xl" style={{
            opacity: capHeaven
          }} data-id="element-276">
              One bite closer to heaven.
            </motion.p>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[9px] tracking-[0.5em] uppercase text-cream/30 z-20" data-id="element-277">
          <span data-id="element-278">Scroll</span>
          <span className="w-px h-8 bg-cream/20" data-id="element-279" />
        </div>
      </div>
    </section>;
}
/* ──────────────────────────────────────────── */
function Lid({
  y,
  scale,
  opacity
}: {
  y: MotionValue<string>;
  scale: MotionValue<number>;
  opacity: MotionValue<number>;
}) {
  return <motion.div className="absolute inset-0 rounded-full overflow-hidden z-10" style={{
    y,
    scale,
    opacity,
    background: `
          radial-gradient(ellipse at 30% 22%, rgba(247,219,180,0.22), transparent 55%),
          radial-gradient(ellipse at 75% 80%, rgba(0,0,0,0.30), transparent 60%),
          repeating-linear-gradient(45deg, rgba(247,240,232,0.045) 0 1px, transparent 1px 5px),
          repeating-linear-gradient(-45deg, rgba(247,240,232,0.045) 0 1px, transparent 1px 5px),
          linear-gradient(155deg, #5a4030 0%, #3a2820 45%, #2a1d16 100%)
        `,
    boxShadow: '0 30px 60px -16px rgba(0,0,0,0.65), inset 0 2px 4px rgba(255,220,180,0.20), inset 0 -16px 30px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,220,180,0.07)'
  }} data-id="element-280">
      {/* Recessed inner ring */}
      <div aria-hidden className="absolute inset-[7%] rounded-full" style={{
      background: 'radial-gradient(ellipse at 30% 25%, rgba(247,219,180,0.10), transparent 65%), linear-gradient(155deg, rgba(0,0,0,0.18), rgba(0,0,0,0.32))',
      boxShadow: 'inset 0 0 0 1px rgba(247,240,232,0.07), inset 0 4px 12px rgba(0,0,0,0.32)'
    }} data-id="element-281" />

      {/* Foil-stamped emblem */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-cream pointer-events-none" data-id="element-282">
        <div style={{
        filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.6)) drop-shadow(0 -1px 0 rgba(255,235,210,0.25))'
      }} data-id="element-283">
          <TartaWordmark height={32} data-id="element-284" />
        </div>
        <span className="mt-2.5 text-[9px] sm:text-[10px] tracking-[0.4em] uppercase text-cream/85" style={{
        textShadow: '0 1px 0 rgba(0,0,0,0.6)'
      }} data-id="element-285">
          A slice of heaven
        </span>
      </div>

      {/* Specular highlight */}
      <div aria-hidden className="absolute inset-0 rounded-full pointer-events-none" style={{
      background: 'radial-gradient(ellipse 38% 24% at 35% 18%, rgba(255,235,210,0.18), transparent 70%)'
    }} data-id="element-286" />
    </motion.div>;
}
function Particles({
  progress
}: {
  progress: MotionValue<number>;
}) {
  // 12 drifting particles with varying positions and timing
  const items = [{
    x: 18,
    delay: 0.1,
    duration: 0.55,
    size: 3
  }, {
    x: 32,
    delay: 0.18,
    duration: 0.5,
    size: 2
  }, {
    x: 45,
    delay: 0.05,
    duration: 0.6,
    size: 4
  }, {
    x: 56,
    delay: 0.22,
    duration: 0.45,
    size: 2
  }, {
    x: 68,
    delay: 0.12,
    duration: 0.55,
    size: 3
  }, {
    x: 80,
    delay: 0.2,
    duration: 0.5,
    size: 2
  }, {
    x: 25,
    delay: 0.3,
    duration: 0.45,
    size: 2
  }, {
    x: 40,
    delay: 0.35,
    duration: 0.4,
    size: 3
  }, {
    x: 60,
    delay: 0.32,
    duration: 0.45,
    size: 2
  }, {
    x: 75,
    delay: 0.38,
    duration: 0.4,
    size: 3
  }, {
    x: 50,
    delay: 0.4,
    duration: 0.45,
    size: 2
  }, {
    x: 35,
    delay: 0.45,
    duration: 0.4,
    size: 2
  }];
  return <div className="absolute inset-0 pointer-events-none" aria-hidden data-id="element-287">
      {items.map((p, i) => <Particle key={i} progress={progress} {...p} data-id="element-288" />)}
    </div>;
}
function Particle({
  progress,
  x,
  delay,
  duration,
  size
}: {
  progress: MotionValue<number>;
  x: number;
  delay: number;
  duration: number;
  size: number;
}) {
  const start = delay;
  const end = Math.min(delay + duration, 0.95);
  const peak = (start + end) / 2;
  const y = useTransform(progress, [start, end], ['100%', '-40%']);
  const opacity = useTransform(progress, [start, peak, end], [0, 0.85, 0]);
  return <motion.span className="absolute rounded-full bg-[#FFE0B8]" style={{
    left: `${x}%`,
    bottom: 0,
    width: size,
    height: size,
    y,
    opacity,
    boxShadow: '0 0 8px rgba(255, 210, 160, 0.7)'
  }} data-id="element-289" />;
}